import { useEffect, useMemo, useRef, useState } from "react";
import { ExternalLink, X } from "lucide-react";
import { getSubApp } from "@perlab/registry";

import { useContextChain, useContextSlice } from "../state/ContextChain";
import { useScopeData } from "../state/useScopeData";
import { ForbiddenError, ScopeSealedOrMissingError, SessionExpiredError } from "../state/denialErrors";
import {
  cancelGleanJob, launchGleanJob, getGleanJob, prepareGleanPrompt,
  openGleanHelper, type GleanHelperConfig, type GleanInstallerConfig, type PreparedGleanPrompt,
} from "../api/gleanHandoff";
import { collectGleanContext, snapshotSelection, type GleanCollectedContext, type GleanScopeContext } from "./contextCollector";
import { gleanFailureMessage } from "./gleanStatus";
import { GleanInstallerDownload } from "./GleanInstallerDownload";
import { useGleanHelperConfig } from "./useGleanHelperConfig";

type Stage = "idle" | "collecting" | "preparing" | "review" | "cancelling" | "opening_helper" | "awaiting_review" | "waiting_helper" | "opening_glean"
  | "sign_in_required" | "submitting" | "submitted" | "uncertain" | "error";

const GLEAN_URL = "https://app.glean.com/";

function errorCode(error: unknown): unknown {
  return error && typeof error === "object" && "code" in error ? error.code : undefined;
}

function handoffErrorMessage(error: unknown, action: "prepare" | "send"): string {
  if (error instanceof SessionExpiredError) return "Your Velocity session expired. Sign in again, then send your selected content.";
  if (error instanceof ForbiddenError || error instanceof ScopeSealedOrMissingError) return "You no longer have access to this content. Open an accessible workspace or select another response.";
  const code = errorCode(error);
  if (code === "GLEAN_SETUP_REQUIRED") return "Send to Glean is not ready for your workspace. Please contact your administrator to finish setup.";
  if (code === "GLEAN_MODEL_UNAVAILABLE") return "Prompt preparation is temporarily unavailable. Please try again shortly. Nothing was sent.";
  if (code === "GLEAN_CONTEXT_INVALID") return "Select a response or document with the topic you want to research, then try again. Nothing was sent.";
  if (action === "prepare") return "We couldn't finish preparing your prompt. Please try again. Nothing was sent.";
  return "We couldn't confirm the handoff to your helper. Check Glean before trying again to avoid sending twice.";
}

export function SendToGlean() {
  const chain = useContextChain();
  const slice = useContextSlice();
  const { fetchScoped, ready } = useScopeData();
  const selection = useRef("");
  const selectionRoot = useRef<Element | null>(null);
  const capturedContext = useRef<GleanCollectedContext | null>(null);
  const abortRef = useRef<AbortController | undefined>(undefined);
  const inFlight = useRef(false);
  const operation = useRef(0);
  const launchStarted = useRef(0);
  const launchUrl = useRef<string | null>(null);
  const launchExpiry = useRef<string | null>(null);
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState<Stage>("idle");
  const [prepared, setPrepared] = useState<PreparedGleanPrompt | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [prompt, setPrompt] = useState("");
  const [helperSetupNeeded, setHelperSetupNeeded] = useState(false);
  const [fallbackNeeded, setFallbackNeeded] = useState(false);
  const { config: helperConfig, loaded: helperConfigLoaded, error: helperConfigError, retry: retryHelperConfig } = useGleanHelperConfig(open);
  const [launchInstallerConfig, setLaunchInstallerConfig] = useState<{
    config: GleanInstallerConfig;
    helperSnapshot: GleanHelperConfig | null;
  } | null>(null);
  // A config refresh supersedes launch guidance, including a launch that returns late.
  const installerConfig = launchInstallerConfig?.helperSnapshot === helperConfig
    ? launchInstallerConfig.config : helperConfig;

  useEffect(() => () => { abortRef.current?.abort(); operation.current += 1; }, []);

  const scope = useMemo<GleanScopeContext>(() => {
    const workspace = chain.workspaces.find((item) => item.id === slice?.rgId);
    const client = chain.clients.find((item) => item.id === slice?.clientId);
    const engagement = chain.engagements.find((item) => item.id === slice?.engagementId);
    const project = chain.projects.find((item) => item.id === slice?.projectId);
    const app = slice?.appId ? getSubApp(slice.appId) : undefined;
    return {
      workspace: workspace && { id: workspace.id, name: workspace.label },
      client: client && { id: client.id, name: client.name },
      engagement: engagement && { id: engagement.id, name: engagement.name },
      project: project && { id: project.id, name: project.label },
      application: app && { id: app.id, name: app.name },
      page_url: window.location.href,
    };
  }, [chain.workspaces, chain.clients, chain.engagements, chain.projects, slice]);

  useEffect(() => {
    if (!jobId || !["opening_helper", "awaiting_review", "waiting_helper", "opening_glean", "sign_in_required", "submitting"].includes(stage)) return;
    let disposed = false;
    const currentOperation = operation.current;
    let polling = false;
    let failures = 0;
    const timer = window.setInterval(() => {
      if (polling) return;
      polling = true;
      void getGleanJob(fetchScoped, jobId).then((job) => {
        if (disposed || currentOperation !== operation.current) return;
        failures = 0;
        const next = job.status === "claimed" ? "opening_glean" : job.status as Stage;
        if (job.status === "prepared") {
          if (launchStarted.current > 0 && Date.now() - launchStarted.current >= (helperConfig?.launch_timeout_seconds ?? 20) * 1_000) {
            setHelperSetupNeeded(true);
            setFallbackNeeded(true);
            setMessage("Glean automation hasn't opened. Allow your browser to open it, or install it on this laptop. Nothing has been sent.");
          }
        } else if (["awaiting_review", "waiting_helper", "opening_glean", "sign_in_required", "submitting", "submitted", "uncertain"].includes(next)) {
          setStage(next);
          setHelperSetupNeeded(false);
          setFallbackNeeded(next === "uncertain");
          setMessage(job.failure_code ? gleanFailureMessage(job.failure_code) : "");
        } else if (["failed", "expired", "cancelled"].includes(job.status)) {
          setStage("error");
          setFallbackNeeded(true);
          setMessage(job.status === "expired" ? "This handoff expired. Collect the context again." : job.status === "cancelled" ? "Cancelled. Unsent work has been stopped." : gleanFailureMessage(job.failure_code));
        }
      }).catch(() => {
        if (disposed || currentOperation !== operation.current) return;
        failures += 1;
        if (failures >= 3) {
          setStage("uncertain");
          setFallbackNeeded(true);
          setMessage("We couldn't confirm the result. Check Glean before sending this prompt again.");
        }
      }).finally(() => { polling = false; });
    }, 2_000);
    return () => { disposed = true; window.clearInterval(timer); };
  }, [fetchScoped, helperConfig, jobId, stage]);

  async function begin(retry = false) {
    if (!ready || inFlight.current) return;
    inFlight.current = true;
    const currentOperation = ++operation.current;
    abortRef.current?.abort();
    setPrepared(null);
    setJobId(null);
    setOpen(true);
    setMessage("");
    setPrompt("");
    setHelperSetupNeeded(false);
    setFallbackNeeded(false);
    setLaunchInstallerConfig(null);
    launchUrl.current = null;
    launchExpiry.current = null;
    launchStarted.current = 0;
    setStage("collecting");
    const context = retry ? capturedContext.current : collectGleanContext(scope, selection.current, document, selectionRoot.current);
    capturedContext.current = context ? structuredClone(context) : null;
    selection.current = "";
    selectionRoot.current = null;
    if (!context) {
      inFlight.current = false;
      setStage("error");
      setMessage("Select content or open a response, report, document, or content panel before sending.");
      return;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setStage("preparing");
    try {
      const result = await prepareGleanPrompt(fetchScoped, context, controller.signal);
      if (controller.signal.aborted || currentOperation !== operation.current) return;
      setPrepared(result);
      if (!result.generated_prompt.trim() || result.generated_prompt.length > 16_000) {
        setStage("error");
        setMessage("We couldn't prepare a usable draft. Select a relevant response or document and try again. Nothing was sent.");
        return;
      }
      const preparedJobId = result.job_id.trim() || null;
      setJobId(preparedJobId);
      setPrompt(result.generated_prompt);
      setStage("review");
      if (!preparedJobId) {
        setFallbackNeeded(true);
        setMessage("You can edit or copy this draft, but sending is not ready. Cancel and prepare it again before continuing.");
      }
    } catch (error) {
      if (controller.signal.aborted || currentOperation !== operation.current) return;
      setStage("error");
      setMessage(handoffErrorMessage(error, "prepare"));
    } finally {
      if (currentOperation === operation.current) inFlight.current = false;
    }
  }

  async function send() {
    if (!jobId || stage !== "review" || inFlight.current || !prompt.trim() || prompt.trim().length > 16_000) return;
    inFlight.current = true;
    const currentOperation = ++operation.current;
    setMessage("");
    setHelperSetupNeeded(false);
    setFallbackNeeded(false);
    setStage("opening_helper");
    try {
      const job = await launchGleanJob(fetchScoped, jobId, prompt.trim());
      if (currentOperation !== operation.current) return;
      launchStarted.current = Date.now();
      launchUrl.current = job.launch_url;
      launchExpiry.current = job.launch_expires_at ?? job.expires_at;
      if (job.helper_install_url !== undefined || job.helper_install_urls !== undefined) {
        setLaunchInstallerConfig({
          helperSnapshot: helperConfig,
          config: {
            ...helperConfig,
            ...(job.helper_install_url !== undefined ? { helper_install_url: job.helper_install_url } : {}),
            ...(job.helper_install_urls !== undefined ? { helper_install_urls: job.helper_install_urls } : {}),
          },
        });
      }
      if (job.status === "awaiting_review") setStage("awaiting_review");
      else if (["waiting_helper", "opening_glean", "sign_in_required", "submitting", "submitted", "uncertain"].includes(job.status)) setStage(job.status as Stage);
      if (job.launch_url) {
        try { openGleanHelper(job.launch_url); } catch {
          setHelperSetupNeeded(true);
          setFallbackNeeded(true);
          setMessage("Glean automation couldn't open. Allow your browser to open it, or install it on this laptop. Nothing has been sent.");
        }
      }
    } catch (error) {
      if (currentOperation !== operation.current) return;
      setStage("uncertain");
      setFallbackNeeded(true);
      setMessage(handoffErrorMessage(error, "send"));
    } finally {
      if (currentOperation === operation.current) inFlight.current = false;
    }
  }

  async function copyDraft() {
    if (stage !== "review" || jobId) return;
    const currentOperation = operation.current;
    try {
      if (!navigator.clipboard) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(prompt);
    } catch {
      if (currentOperation !== operation.current) return;
      setMessage("Copy was unavailable. Select and copy the prompt text above.");
    }
  }

  async function cancel() {
    if (stage === "cancelling") return;
    abortRef.current?.abort();
    operation.current += 1;
    inFlight.current = false;
    capturedContext.current = null;
    launchUrl.current = null;
    if (jobId && !["submitting", "submitted", "uncertain"].includes(stage)) {
      setStage("cancelling");
      try {
        await cancelGleanJob(fetchScoped, jobId);
      } catch {
        setStage("uncertain");
        setFallbackNeeded(true);
        setMessage("We couldn't confirm cancellation. Check Glean before sending again; a message already submitted cannot be retracted.");
        return;
      }
    }
    setStage("idle"); setOpen(false); setPrepared(null); setJobId(null); setMessage("");
  }

  const busy = stage !== "idle" && stage !== "error" && stage !== "submitted" && stage !== "uncertain";
  const contextNotes = [...new Set([
    ...(prepared?.warnings ?? []),
    ...(prepared?.requires_clarification && prepared.clarification_question?.trim()
      ? [`Optional detail you can add to the draft: ${prepared.clarification_question.trim()}`]
      : []),
  ])];
  return (
    <>
      <button
        type="button" aria-label="Send selected context to Glean" title="Send to Glean"
        aria-disabled={!ready || busy}
        onPointerDown={() => {
          selection.current = snapshotSelection();
          const anchor = window.getSelection()?.anchorNode;
          const element = anchor instanceof Element ? anchor : anchor?.parentElement;
          selectionRoot.current = element?.closest('[data-glean-context-active="true"], [data-glean-response-active="true"], [data-glean-selected="true"]') ?? null;
        }}
        onClick={() => { if (!busy) { setStage("idle"); void begin(); } }}
        className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-sm font-semibold text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
      ><span aria-hidden>S</span></button>
      {open && (
        <div className="fixed inset-0 z-[var(--z-dialog)] flex items-center justify-center bg-black/60 p-4" role="presentation">
          <section role="dialog" aria-modal="true" aria-labelledby="glean-title" className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-[var(--radius-container)] border border-[var(--color-border-default)] bg-[var(--color-surface-elevated)] p-5 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <h2 id="glean-title" className="text-lg font-semibold">Send to Glean</h2>
              <button type="button" aria-label="Close Send to Glean" onClick={() => void cancel()}><X size={18} /></button>
            </div>
            <p aria-live="polite" className="mt-4 text-sm text-[var(--color-text-secondary)]">{stageLabel(stage)}</p>
            {message && <p role="alert" className="mt-2 text-sm text-[var(--color-system-status-error)]">{message}</p>}
            {stage === "review" && <textarea aria-label="Prompt to send to Glean" value={prompt} onChange={(event) => setPrompt(event.target.value)} maxLength={16_000} rows={10} className="mt-4 w-full resize-y rounded border border-[var(--color-border-default)] bg-[var(--color-surface-muted)] p-3 text-sm leading-relaxed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]" />}
            {stage === "review" && !!contextNotes.length && <details className="mt-2 text-sm text-[var(--color-text-secondary)]"><summary>Optional context notes</summary>{contextNotes.map((warning, index) => <p key={index}>{warning}</p>)}</details>}
            {(helperSetupNeeded || stage === "review") && <div className="mt-4 space-y-2 rounded border border-[var(--color-border-default)] p-3 text-sm">
              <GleanInstallerDownload config={installerConfig} loaded={helperConfigLoaded} error={helperConfigError} onRetry={retryHelperConfig} />
              {launchUrl.current && <button type="button" className="block rounded border px-3 py-2" onClick={() => {
                if (launchUrl.current && launchExpiry.current && Date.parse(launchExpiry.current) > Date.now()) {
                  try { openGleanHelper(launchUrl.current); } catch { setMessage("Your browser couldn't open Glean automation. Check that it is installed."); }
                } else setMessage("This handoff expired. Cancel and prepare your prompt again.");
              }}>Open Glean automation</button>}
            </div>}
            {fallbackNeeded && <details className="mt-3 text-sm text-[var(--color-text-secondary)]">
              <summary>Other options</summary>
              <div className="mt-2 flex flex-wrap gap-2">
                {prompt && stage === "review" && !jobId && <button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => void copyDraft()}>Copy prompt</button>}
                <a href={helperConfig?.glean_url ?? GLEAN_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded border px-3 py-2 text-sm">Open Glean <ExternalLink size={14} /></a>
              </div>
            </details>}
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              {stage === "error" && !jobId && <button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => void begin(true)}>Try again</button>}
              <button type="button" disabled={stage === "cancelling"} className="rounded border px-3 py-2 text-sm" onClick={() => void cancel()}>{["submitting", "submitted", "uncertain"].includes(stage) ? "Close" : "Cancel"}</button>
              {stage === "review" && <button type="button" disabled={!jobId || !prompt.trim() || prompt.trim().length > 16_000} className="rounded bg-[var(--color-accent)] px-3 py-2 text-sm text-black disabled:opacity-50" onClick={() => void send()}>Continue</button>}
            </div>
          </section>
        </div>
      )}
    </>
  );
}

function stageLabel(stage: Stage): string {
  return ({ idle: "Ready", collecting: "Collecting context", preparing: "Prompting agent is preparing your prompt…", review: "Review and edit your prompt, then continue to open Glean automation.", cancelling: "Cancelling unsent work…", opening_helper: "Opening Glean automation…", awaiting_review: "Review the prompt in the opened Velocity tab, then choose Approve.", waiting_helper: "Starting your approved request…", opening_glean: "Opening Glean…", sign_in_required: "Sign in to Glean in the automation browser to continue.", submitting: "Submitting your prompt…", submitted: "Sent to Glean", uncertain: "Submission uncertain — check Glean before resending", error: "Action required" })[stage];
}
